<section class="py-8 md:py-12 lg:py-16">
  <div class="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
    <!-- Loading skeleton when order details are not yet loaded -->
    <section *ngIf="!orderDetails" class="w-full">
      <nz-skeleton class="w-full" [nzActive]="true"></nz-skeleton>
      <nz-skeleton [nzActive]="true"></nz-skeleton>
      <nz-skeleton [nzActive]="true"></nz-skeleton>
    </section>

    <div *ngIf="orderDetails" class="space-y-6">
      <!-- Progress Steps -->
      <div class="my-6 md:my-8">
        <div class="hidden md:block">
          <nz-steps [nzCurrent]="2">
            <nz-step nzTitle="Select Event" nzStatus="finish"></nz-step>
            <nz-step
              nzTitle="Review Cart"
              nzStatus="finish"
              nzIcon="shopping-cart"
            ></nz-step>
            <nz-step nzTitle="Pay" nzStatus="process" nzIcon="dollar"></nz-step>
          </nz-steps>
        </div>
        <div class="md:hidden">
          <nz-steps [nzCurrent]="2" nzDirection="vertical">
            <nz-step nzTitle="Select Event" nzStatus="finish"></nz-step>
            <nz-step
              nzTitle="Review Cart"
              nzStatus="finish"
              nzIcon="shopping-cart"
            ></nz-step>
            <nz-step nzTitle="Pay" nzStatus="process" nzIcon="dollar"></nz-step>
          </nz-steps>
        </div>
      </div>

      <!-- Header -->
      <header class="text-center">
        <h1 class="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Checkout
        </h1>
        <p class="text-gray-600">Order #{{ orderDetails.order.orderNumber }}</p>
      </header>

      <!-- Compact Payment Summary -->
      <div class="max-w-2xl mx-auto">
        <div
          class="bg-white rounded-lg border border-gray-200 shadow-sm p-6 md:p-8"
        >
          <!-- Cost Breakdown -->
          <div class="space-y-4 mb-6">
            <h2
              class="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2"
            >
              Payment Summary
            </h2>

            <!-- Ticket Costs -->
            <div>
              <h3 class="font-medium text-gray-800 mb-3">Tickets</h3>
              <div class="space-y-2">
                <div
                  *ngFor="let orderItem of orderDetails?.orderItems"
                  class="flex justify-between gap-4 text-sm"
                >
                  <span class="text-gray-600">
                    <span class="block">{{ orderItem?.productName }}</span>
                    <span
                      *ngIf="hasSelectedAddOns(orderItem)"
                      class="block text-xs text-gray-500"
                    >
                      Add-ons: {{ addOnsText(orderItem) }}
                    </span>
                  </span>
                  <span class="font-medium">{{
                    orderItem?.amount | currency:getCurrencyCode()
                  }}</span>
                </div>
              </div>
            </div>

            <!-- Order Fees -->
            <div *ngIf="fees && fees.length > 0">
              <h3 class="font-medium text-gray-800 mb-3">Additional Fees</h3>
              <div class="space-y-2">
                <div
                  *ngFor="let orderFee of fees"
                  class="flex justify-between text-sm"
                >
                  <span class="text-gray-600">{{ orderFee?.fee?.name }}</span>
                  <span class="font-medium">{{
                    (orderFee?.amount || orderFee?.fee?.amount)
                      | currency:getCurrencyCode()
                  }}</span>
                </div>
              </div>
            </div>

            <div *ngIf="shouldShowDeliveryFee()" class="space-y-2">
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">Delivery Fee</span>
                <span class="font-medium">{{
                  getDeliveryFee() | currency:getCurrencyCode()
                }}</span>
              </div>
            </div>

            <!-- keeps the lines adding up to the total when the backend's amount differs from them (rounding, a fee not in the list) -->
            <div *ngIf="shouldShowAdjustment()" class="flex justify-between text-sm">
              <span class="text-gray-600">Adjustment</span>
              <span class="font-medium">{{
                getAdjustmentAmount() | currency:getCurrencyCode()
              }}</span>
            </div>

            <!-- Amount to Pay: what the backend charges (order.amount + order.processingFee) -->
            <div class="border-t border-gray-200 pt-4">
              <div class="flex justify-between items-center">
                <span class="text-lg font-semibold text-gray-900"
                  >Amount to Pay</span
                >
                <span class="text-xl font-bold text-green-600">
                  {{ calculateTotal() | currency:getCurrencyCode() }}
                </span>
              </div>
            </div>
          </div>

          <!-- Seat hold countdown: only for orders with chosen seats. At zero the seats are released and
               the buyer is refused and sent back to choose again, so there is no way to carry on paying. -->
          <div
            *ngIf="seatSecondsLeft !== null"
            role="timer"
            data-testid="seat-hold-countdown"
            class="flex items-center justify-between gap-4 rounded-lg border p-4 mb-6"
            [ngClass]="
              seatHoldLow
                ? 'bg-red-50 border-red-300 text-red-800'
                : 'bg-blue-50 border-blue-200 text-blue-900'
            "
          >
            <p class="text-sm">
              <span class="font-bold">Your seats are held for you.</span>
              Complete payment before the time runs out or they are released.
            </p>
            <span
              class="text-2xl font-bold tabular-nums flex-shrink-0"
              data-testid="seat-hold-countdown-text"
              >{{ seatCountdownText }}</span
            >
          </div>

          <!-- Email NB -->
          <div
            class="flex gap-3 bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6"
          >
            <span
              nz-icon
              nzType="warning"
              nzTheme="fill"
              class="text-amber-500 text-xl flex-shrink-0 mt-0.5"
            ></span>
            <p class="text-sm text-amber-800">
              <span class="font-bold">NB:</span> Your tickets will be sent to
              your registered email address after payment. Please ensure your
              email address is correct and that your inbox is not full before
              proceeding.
            </p>
          </div>

          <!-- Payment Button -->
          <div class="space-y-3">
            <button
              (click)="initiatePaymentWithOrderNumber()"
              [disabled]="checkoutLoading || seatSecondsLeft === 0"
              class="w-full bg-black hover:bg-gray-800 disabled:bg-gray-400 text-white font-bold py-3 px-6 rounded-lg flex items-center justify-center transition-colors duration-200"
            >
              <span *ngIf="!checkoutLoading" class="flex items-center">
                <svg
                  class="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  ></path>
                </svg>
                Proceed to Payment
              </span>
              <span *ngIf="checkoutLoading" class="flex items-center">
                <img
                  src="assets/loading.gif"
                  alt="Loading"
                  class="w-5 h-5 mr-2"
                />
                Processing...
              </span>
            </button>

            <p class="text-xs text-gray-500 text-center">
              You will be redirected to our secure payment gateway
            </p>

            <!-- Back to cart: cancel this pending order and return to edit/clear the cart. -->
            <button
              *ngIf="canCancelOrder"
              (click)="backToCart()"
              [disabled]="cancelling || checkoutLoading"
              class="w-full bg-white hover:bg-gray-50 disabled:opacity-50 text-gray-700 font-medium py-2.5 px-6 rounded-lg border border-gray-300 transition-colors duration-200"
            >
              <span *ngIf="!cancelling">Back to cart</span>
              <span *ngIf="cancelling">Cancelling…</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>
